import type { Area } from './types';
import { liveAreas } from './types';

/**
 * AREA NAME MATCHING — shared by the Area combobox's "Did you mean …?" nudge and
 * the Epic field's "that looks like an area" nudge, so both judge "close enough to
 * be the same area" the same way.
 */

/**
 * True when two area names are close enough to be the same thing typed twice —
 * one contains the other ("Network" / "Networks"), or they are within two edits
 * ("Netwrok"). EQUAL names are not near (there is nothing to nudge toward).
 */
export function near(a: string, b: string): boolean {
  const x = a.toLowerCase().trim();
  const y = b.toLowerCase().trim();
  if (!x || !y || x === y) return false;
  if (x.includes(y) || y.includes(x)) return true;
  const m = x.length;
  const n = y.length;
  if (Math.abs(m - n) > 2) return false;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  let cur = new Array<number>(n + 1);
  for (let i = 1; i <= m; i++) {
    cur[0] = i;
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (x[i - 1] === y[j - 1] ? 0 : 1));
    }
    [prev, cur] = [cur, prev];
  }
  return prev[n] <= 2;
}

/**
 * The live area a free-text value most likely MEANS — an exact name match first,
 * then a near one — or undefined when nothing is close. Retired areas never
 * match: they are not on offer for new work.
 */
export function areaForText(text: string, areas: Area[]): Area | undefined {
  const t = text.trim().toLowerCase();
  if (!t) return undefined;
  const live = liveAreas(areas);
  return live.find((a) => a.name.toLowerCase().trim() === t) ?? live.find((a) => near(a.name, text));
}
